import * as React from "react";
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import TaskIcon from './TaskIcon';
import { MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOut } from 'phosphor-react';

const FLOOR_MAP = process.env.PUBLIC_URL + '/Floor.png';
const GARDEN_MAP = process.env.PUBLIC_URL + '/Garden.png';

const COLOR_MAPPINGS = {
  1: 'green',   // Green #0ED145
  2: 'red',     // Red #EC1C24
  3: 'pink',    // Pink #FF01C0
  4: '#B83DBA', // Purple/Blue #B83DBA
  5: 'blue',    // Blue #1100FF
  7: 'orange',  // Orange #FF6600
  8: 'yellow',  // Yellow #FFDD01
  9: '#8000FF', // Purple #8000FF
  10: '#FE9FFE', // Pink #FE9FFE
};

const Plan = ({ onTaskSelect, isTaskLocked, isGloballyLocked }) => {
  const [currentMap, setCurrentMap] = useState('garden');
  const [tasks, setTasks] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const [initialPinchDistance, setInitialPinchDistance] = useState(null);
  const [initialZoom, setInitialZoom] = useState(1);
  const [colorPositions, setColorPositions] = useState({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);



useEffect(() => {
  const handleResize = () => setWindowWidth(window.innerWidth);
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);


  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const username = localStorage.getItem('username');
        const response = await axios.get(`${API_BASE_URL}/tasks`, {
          params: { username }
        });
        setTasks(response.data);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    fetchTasks();
    const interval = setInterval(fetchTasks, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const getMapCoordinates = (clientX, clientY) => {
    if (!mapRef.current) return { x: 0, y: 0 };
    
    const rect = mapRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, (
      (clientX - rect.left) / rect.width * 100
    ))).toFixed(2);
    const y = Math.max(0, Math.min(100, (
      (clientY - rect.top) / rect.height * 100
    ))).toFixed(2);
    
    return { x, y };
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      e.stopPropagation();
      e.preventDefault();
      const newPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };
      setPosition(newPosition);
    }
    const coords = getMapCoordinates(e.clientX, e.clientY);
    setMousePos(coords);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoom = (direction) => {
    setZoom(prev => {
      const newZoom = direction === 'in' ? prev + 0.1 : prev - 0.1;
      return Math.min(Math.max(newZoom, 0.5), 2);
    });
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const getTaskStatus = (task) => {
    if (isGloballyLocked) {
      return "Zadanie zablokowane przez administratora";
    }
    if (task.tasksLocked) {
      return "Zadanie zablokowane przez administratora";
    }
    if (task.minutes_remaining > 0) {
      return `Można wykonać ponownie za ${task.minutes_remaining} min`;
    }
    if (task.completed_count >= task.max_count) {
      return `Wykonano ${task.completed_count}/${task.max_count}`;
    }
    return "Wykonaj zadanie";
  };

  const handleTaskSelect = (taskId) => {
    setIsExpanded(false); // Wyłącz tryb rozszerzony przed skanowaniem
    onTaskSelect(taskId);
  };

  const detectColors = (mapImage) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true, imageSmoothingEnabled: false });
    
    // Preserve original image dimensions
    canvas.width = mapImage.naturalWidth;
    canvas.height = mapImage.naturalHeight;
    
    // Disable image smoothing
    ctx.imageSmoothingEnabled = false;
    ctx.webkitImageSmoothingEnabled = false;
    ctx.mozImageSmoothingEnabled = false;
    ctx.msImageSmoothingEnabled = false;
    
    ctx.drawImage(mapImage, 0, 0);

    const foundPositions = {};
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    const mapSettings = {
      garden: {
        tolerance: 2,
        colors: {
          1: { r: 14, g: 209, b: 69, tolerance: 1 },    // Green
          2: { r: 236, g: 28, b: 36, tolerance: 1 },    // Red
          3: { r: 255, g: 1, b: 192, tolerance: 1 },    // Pink
          4: { r: 184, g: 61, b: 186, tolerance: 1 },   // Purple/Blue
          5: { r: 17, g: 0, b: 255, tolerance: 1 },     // Blue
          8: { r: 255, g: 221, b: 1, tolerance: 1 },     // Yellow
          9: { r: 128, g: 0, b: 255, tolerance: 1 },     // Purple
          10: { r: 254, g: 159, b: 254, tolerance: 1 }   // Pink
        }
      },
      floor: {
        tolerance: 2,
        colors: {
          1: { r: 14, g: 209, b: 69, tolerance: 1 },
          2: { r: 236, g: 28, b: 36, tolerance: 1 },
          3: { r: 255, g: 1, b: 192, tolerance: 1 },
          4: { r: 184, g: 61, b: 186, tolerance: 1 },
          5: { r: 17, g: 0, b: 255, tolerance: 1 },
          8: { r: 255, g: 221, b: 1, tolerance: 1 },
          7: { r: 255, g: 102, b: 0, tolerance: 1 },
          9: { r: 128, g: 0, b: 255, tolerance: 1 },     // Purple
          10: { r: 254, g: 159, b: 254, tolerance: 1 }   // Pink
        }
      }
    };

    const settings = mapSettings[currentMap];
    const colorRegions = {
      1: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      2: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      3: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      4: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      5: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      7: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      8: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      9: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] },
      10: { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, count: 0, pixels: [] }
    };

    // Scan with larger steps but store exact matches
    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        const i = (y * canvas.width + x) * 4;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        Object.entries(settings.colors).forEach(([taskId, color]) => {
          if (Math.abs(r - color.r) <= settings.tolerance &&
              Math.abs(g - color.g) <= settings.tolerance &&
              Math.abs(b - color.b) <= settings.tolerance) {
            const region = colorRegions[taskId];
            region.pixels.push({x, y});
            region.minX = Math.min(region.minX, x);
            region.maxX = Math.max(region.maxX, x);
            region.minY = Math.min(region.minY, y);
            region.maxY = Math.max(region.maxY, y);
            region.count++;
          }
        });
      }
    }

    Object.entries(colorRegions).forEach(([taskId, region]) => {
      if (region.count > 0) {
        console.log(`Task ${taskId} pixels found:`, region.count);
        
        // Find the most dense cluster of pixels
        const pixelGroups = [];
        const searchRadius = 5;
        
        region.pixels.forEach(pixel => {
          let added = false;
          for (const group of pixelGroups) {
            const centerX = group.x / group.count;
            const centerY = group.y / group.count;
            if (Math.abs(pixel.x - centerX) <= searchRadius && 
                Math.abs(pixel.y - centerY) <= searchRadius) {
              group.x += pixel.x;
              group.y += pixel.y;
              group.count++;
              added = true;
              break;
            }
          }
          if (!added) {
            pixelGroups.push({ x: pixel.x, y: pixel.y, count: 1 });
          }
        });

        // Use the largest group's center
        const largestGroup = pixelGroups.reduce((max, group) => 
          group.count > max.count ? group : max, pixelGroups[0]);

        const centerX = Math.round(largestGroup.x / largestGroup.count);
        const centerY = Math.round(largestGroup.y / largestGroup.count);

        console.log(`Task ${taskId} center:`, { x: centerX, y: centerY });
        
        foundPositions[taskId] = {
          x: (centerX / canvas.width) * 100,
          y: (centerY / canvas.height) * 100,
          mapType: currentMap,
          pixelX: centerX,
          pixelY: centerY
        };
      }
    });

    return foundPositions;
  };

  const renderTaskMarker = (task) => {
    if (task.id !== 1 && task.id !== 2 && task.id !== 3 && task.id !== 4 && 
        task.id !== 5 && task.id !== 7 && task.id !== 8 && task.id !== 9 && 
        task.id !== 10) return null;
    
    const mapPositions = colorPositions[currentMap];
    const position = mapPositions?.[task.id];
    
    if (!position || position.mapType !== currentMap) return null;

    const isMobile = windowWidth < 768;
    const size = isMobile ? 20 : 25;
    
    const isDisabled = isTaskLocked(task) || 
                      isGloballyLocked || 
                      task.minutes_remaining > 0 ||
                      (task.completed_count >= task.max_count);

    const showMinutes = task.minutes_remaining > 0;
    const statusText = showMinutes ? 
      `${task.minutes_remaining} min` : 
      isDisabled ? `Niedostępne` : 'Wykonaj zadanie';

    return (
      <div
        key={task.id}
        className={`absolute transition-all duration-300
          ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
        style={{
          left: `${position.pixelX}px`,
          top: `${position.pixelY}px`,
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px', // Doubled gap
          zIndex: 20
        }}
        onClick={() => !isDisabled && handleTaskSelect(task.id)}
        title={task.name}
      >
        <div className="relative" style={{ width: `${size}px`, height: `${size}px` }}>
          <TaskIcon type={task.type} points={task.points} />
        </div>
        <div 
          className={`text-center whitespace-nowrap px-3 py-1.5 rounded bg-gray-800/90 text-white
            ${isMobile ? 'text-xs' : 'text-sm'}`} // Increased text size
          style={{
            marginTop: '8px' // Doubled margin
          }}
        >
          {statusText}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    // Force high quality image loading
    img.setAttribute('decoding', 'sync');
    img.setAttribute('loading', 'eager');
    
    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        console.error('Invalid image dimensions');
        return;
      }
      const positions = detectColors(img);
      setColorPositions(prev => ({
        ...prev,
        [currentMap]: positions
      }));
    };
    
    img.onerror = (e) => {
      console.error('Image load error:', e);
    };
    
    img.src = currentMap === 'floor' ? FLOOR_MAP : GARDEN_MAP;
  }, [currentMap]);

  // Add debug visualization
  const renderColorDebug = () => {
    const positions = colorPositions[currentMap];
    if (!positions) return null;

    return Object.entries(positions).map(([taskId, pos]) => {
      let color = 'gray';
      switch (parseInt(taskId)) {
        case 1: color = '#0ED145'; break; // green
        case 2: color = '#EC1C24'; break; // red
        case 3: color = '#FF01C0'; break; // pink
        case 4: color = '#B83DBA'; break; // purple/blue
        case 5: color = '#1100FF'; break; // blue
        case 7: color = '#FF6600'; break; // orange
        case 8: color = '#FFDD01'; break; // yellow
        case 9: color = '#8000FF'; break; // purple
        case 10: color = '#FE9FFE'; break; // pink
      }
      
      return (
        <div
          key={`debug-${taskId}`}
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${pos.x}%`,
            top: `${pos.y}%`,
            zIndex: 1000,
          }}
        >
          <div 
            className="w-3 h-3 rounded-full border-2 border-white shadow-md"
            style={{ backgroundColor: color }}
          />
        </div>
      );
    });
  };

  const getDistance = (touch1, touch2) => {
    return Math.hypot(
      touch1.clientX - touch2.clientX,
      touch1.clientY - touch2.clientY
    );
  };

  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent default for all touch events
    e.stopPropagation();
    
    if (e.touches.length === 2) {
      const distance = getDistance(e.touches[0], e.touches[1]);
      setInitialPinchDistance(distance);
      setInitialZoom(zoom);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isDragging && e.touches.length === 1) return;

    if (e.touches.length === 2) {
      const distance = getDistance(e.touches[0], e.touches[1]);
      if (initialPinchDistance) {
        const scale = distance / initialPinchDistance;
        const newZoom = Math.min(Math.max(initialZoom * scale, 0.5), 2);
        setZoom(newZoom);
      }
    } else if (isDragging && e.touches.length === 1) {
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setInitialPinchDistance(null);
    setInitialZoom(zoom);
  };

  useEffect(() => {
    // Reset position when changing maps
    setPosition({ x: 0, y: 0 });
    setZoom(1);
  }, [currentMap]);

  useEffect(() => {
    let resizeTimer;
    const handleResize = () => {
      // Reset zoom and position during resize
      setZoom(1);
      setPosition({ x: 0, y: 0 });

      // Debounce the color detection
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          const positions = detectColors(img);
          setColorPositions(prev => ({
            ...prev,
            [currentMap]: positions
          }));
        };
        img.src = currentMap === 'floor' ? FLOOR_MAP : GARDEN_MAP;
      }, 200); // Wait for resize to finish
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [currentMap]);

  // Add container resize observer
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleWindowResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const handleImageLoad = (e) => {
    setImageDimensions({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight
    });
  };

  // Add mouse event handlers for map dragging
  const handleMapMouseDown = (e) => {
    if (e.button !== 0) return; // Only left click
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMapMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const newPosition = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };
    setPosition(newPosition);
  };

  const handleMapMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div 
      ref={containerRef}
      style={{
        position: isExpanded ? 'fixed' : 'relative',
        top: isExpanded ? 0 : undefined,
        left: isExpanded ? 0 : undefined,
        right: isExpanded ? 0 : undefined,
        bottom: isExpanded ? 0 : undefined,
        zIndex: isExpanded ? 9999 : undefined,
      }}
      className={`bg-gray-900 shadow-lg overflow-hidden transition-all duration-300 ${
        isExpanded 
          ? 'w-screen h-screen' 
          : 'h-[70vh] md:h-[80vh] rounded-xl border border-gray-700'
      }`}
    >
      {/* Controls container */}
      <div className="absolute top-4 right-4 z-50">
        <div className="flex gap-1 bg-gray-800/90 backdrop-blur rounded-lg shadow-lg p-1 border border-gray-700">
          <button
            onClick={() => handleZoom('out')}
            className="p-2 hover:bg-gray-700 rounded-lg disabled:opacity-50 transition-all text-gray-300"
            disabled={zoom <= 0.5}
          >
            <MagnifyingGlassMinus size={20} />
          </button>
          <button
            onClick={() => handleZoom('in')}
            className="p-2 hover:bg-gray-700 rounded-lg disabled:opacity-50 transition-all text-gray-300"
            disabled={zoom >= 2}
          >
            <MagnifyingGlassPlus size={20} />
          </button>
          <div className="w-px bg-gray-700"></div>
          <button
            onClick={toggleExpand}
            className={`p-2 hover:bg-gray-700 rounded-lg transition-all text-gray-300 ${
              isExpanded ? 'bg-gray-700' : ''
            }`}
          >
            <ArrowsOut size={20} />
          </button>
        </div>
      </div>

      {/* Map type buttons */}
      <div className="absolute top-4 left-4 z-50 flex gap-1 bg-gray-800/90 backdrop-blur rounded-lg shadow-lg p-1 border border-gray-700">
        <button
          onClick={() => setCurrentMap('garden')}
          className={`px-4 py-2 rounded-lg transition-all text-sm md:text-base ${
            currentMap === 'garden' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-300 hover:bg-gray-700'
          }`}
        >
          Teren
        </button>
        <button
          onClick={() => setCurrentMap('floor')}
          className={`px-4 py-2 rounded-lg transition-all text-sm md:text-base ${
            currentMap === 'floor' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-300 hover:bg-gray-700'
          }`}
        >
          Budynek
        </button>
      </div>

      {/* Map container */}
      <div 
        ref={mapRef} 
        className={`absolute inset-0 touch-none overflow-hidden ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMapMouseDown}
        onMouseMove={handleMapMouseMove}
        onMouseUp={handleMapMouseUp}
        onMouseLeave={handleMapMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div className="absolute inset-0" style={{
          transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
          transformOrigin: '0 0',
          width: `${imageDimensions.width}px`,
          height: `${imageDimensions.height}px`,
          willChange: 'transform'
        }}>
          <img 
            onLoad={handleImageLoad}
            src={currentMap === 'floor' ? FLOOR_MAP : GARDEN_MAP}
            alt={`Plan ${currentMap === 'floor' ? 'budynku' : 'terenu'}`}
            className="w-full h-full object-contain select-none rounded-lg shadow-lg"
            crossOrigin="anonymous"
            draggable="false"
          />
          {renderColorDebug()} {/* Add debug visualization */}
          <div className="absolute inset-0">
            {tasks
              .filter(task => !task.name?.startsWith('UKRYJ') && task.location === currentMap)
              .map(renderTaskMarker)}
          </div>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default Plan;

